import React, { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FileText,
  Upload,
  Download,
  CheckCircle,
  AlertCircle,
  X,
  Search,
} from 'lucide-react';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import Badge from '../../components/ui/Badge.jsx';
import { formatDate } from '../../utils/formatters.js';
import { toast } from 'react-hot-toast';
import { form16API, getApiErrorMessage } from '../../lib/api.js';
import { isAdminOrAbove } from '../../lib/rbac.js';

/**
 * Form 16 tax certificates.
 *
 * Admins upload a ZIP of pre-split per-employee PDFs (named `<PAN>_<FY>.pdf`);
 * the server matches each to a user by PAN and stores it. The import is a
 * two-step flow: preview (dry run) → confirm. Employees see only their own
 * certificates with a download link.
 */
function Form16Page() {
  const admin = isAdminOrAbove();
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);

  const [search, setSearch] = useState('');
  const [pendingFile, setPendingFile] = useState(null);
  const [preview, setPreview] = useState(null); // dry-run summary before confirm
  const [downloadingId, setDownloadingId] = useState(null);

  // Admin sees all Form 16s; employees see only their own.
  const listQuery = useQuery({
    queryKey: ['form16', admin ? 'all' : 'mine'],
    queryFn: () => (admin ? form16API.getAll() : form16API.getMine()),
  });
  const rows = listQuery.data?.data?.data ?? [];

  const previewMutation = useMutation({
    mutationFn: (file) => {
      const fd = new FormData();
      fd.append('zipFile', file);
      return form16API.previewZip(fd);
    },
    onSuccess: (res) => setPreview(res.data),
    onError: (err) => toast.error(getApiErrorMessage(err, 'Failed to read the ZIP')),
  });

  const uploadMutation = useMutation({
    // Prefer the staged file from preview so we don't re-upload the ZIP; fall
    // back to the original File only if staging is somehow unavailable.
    mutationFn: () => {
      const fd = new FormData();
      if (preview?.staged) fd.append('staged', preview.staged);
      else fd.append('zipFile', pendingFile);
      return form16API.uploadZip(fd);
    },
    onSuccess: (res) => {
      toast.success(res.data?.message || 'Form 16 import complete');
      resetImport();
      queryClient.invalidateQueries({ queryKey: ['form16'] });
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Import failed')),
  });

  // Download links expire quickly, so mint a fresh one on click instead of
  // embedding it in the (cached) list.
  const handleDownload = async (row) => {
    setDownloadingId(row.id);
    try {
      const res = await form16API.getDownloadLink(row.id);
      const url = res.data?.url;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
      else toast.error('Could not get download link');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Download failed'));
    } finally {
      setDownloadingId(null);
    }
  };

  const resetImport = () => {
    setPendingFile(null);
    setPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.zip')) {
      toast.error('Please select a .zip file');
      return;
    }
    setPendingFile(file);
    setPreview(null);
    previewMutation.mutate(file);
  };

  const filtered = rows.filter((r) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      r.pan_no?.toLowerCase().includes(q) ||
      r.financial_year?.toLowerCase().includes(q) ||
      r.user_name?.toLowerCase().includes(q) ||
      r.dgp_id?.toLowerCase().includes(q) ||
      r.file_name?.toLowerCase().includes(q)
    );
  });

  const s = preview?.summary;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-brand-primary/10">
            <FileText className="h-6 w-6 text-brand-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Form 16</h1>
            <p className="text-sm text-gray-500">
              {admin
                ? 'Import and manage employee Form 16 tax certificates.'
                : 'Download your Form 16 tax certificates.'}
            </p>
          </div>
        </div>
      </div>

      {/* Admin import panel */}
      {admin && (
        <div className="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-900">Import Form 16 (ZIP)</h2>
            {(pendingFile || preview) && (
              <button
                onClick={resetImport}
                className="text-gray-400 hover:text-gray-600"
                title="Clear"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>

          <p className="text-sm text-gray-500">
            Upload a single <code>.zip</code> containing the per-employee PDFs
            named <code>&lt;PAN&gt;_&lt;FY&gt;.pdf</code> (e.g.{' '}
            <code>AABPU6368G_2026-27.pdf</code>). Each PDF is matched to a user by PAN.
          </p>

          <div className="flex items-center gap-3">
            <input
              ref={fileInputRef}
              type="file"
              accept=".zip"
              onChange={onPickFile}
              className="hidden"
              id="form16-zip-input"
            />
            <Button
              variant="secondary"
              onClick={() => fileInputRef.current?.click()}
              disabled={previewMutation.isPending || uploadMutation.isPending}
            >
              <Upload className="h-4 w-4 mr-2" />
              {pendingFile ? 'Choose a different ZIP' : 'Select ZIP'}
            </Button>
            {pendingFile && (
              <span className="text-sm text-gray-600 truncate max-w-xs">
                {pendingFile.name}
              </span>
            )}
            {previewMutation.isPending && (
              <span className="text-sm text-gray-500">Reading ZIP…</span>
            )}
          </div>

          {/* Preview summary → confirm */}
          {s && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="Valid PDFs" value={s.validPdfs} />
                <Stat label="New" value={s.wouldStore} tone="pos" />
                <Stat label="Will replace" value={s.wouldReplace} tone="warn" />
                <Stat label="No matching user" value={s.wouldFail} tone="neg" />
              </div>
              <p className="text-sm text-gray-600">{preview.message}</p>
              {s.financialYears?.length > 0 && (
                <p className="text-xs text-gray-500">
                  Financial year(s): {s.financialYears.join(', ')}
                </p>
              )}
              {preview.unmatched?.length > 0 && (
                <details className="text-xs text-gray-500">
                  <summary className="cursor-pointer">
                    {preview.unmatched.length} unmatched PAN(s) will be skipped
                  </summary>
                  <div className="mt-1 max-h-32 overflow-auto font-mono">
                    {preview.unmatched.map((u) => (
                      <div key={u.pan}>
                        {u.pan} — {u.fileName}
                      </div>
                    ))}
                  </div>
                </details>
              )}
              <div className="flex items-center gap-2 pt-1">
                <Button
                  onClick={() => uploadMutation.mutate()}
                  disabled={uploadMutation.isPending || s.validPdfs === 0}
                >
                  {uploadMutation.isPending ? (
                    'Importing…'
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Confirm import ({s.wouldStore + s.wouldReplace})
                    </>
                  )}
                </Button>
                <Button variant="ghost" onClick={resetImport} disabled={uploadMutation.isPending}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Listing */}
      <div className="rounded-xl border border-gray-200 bg-white">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h2 className="text-lg font-medium text-gray-900">
            {admin ? 'All Form 16 certificates' : 'My Form 16 certificates'}
          </h2>
          {rows.length > 0 && (
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                className="pl-9"
                placeholder="Search PAN, year, name…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          )}
        </div>

        {listQuery.isLoading ? (
          <div className="p-8 text-center text-gray-500">Loading…</div>
        ) : listQuery.isError ? (
          <div className="p-8 text-center text-red-500 flex items-center justify-center gap-2">
            <AlertCircle className="h-5 w-5" /> Failed to load Form 16 records.
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {rows.length === 0 ? 'No Form 16 certificates yet.' : 'No matches.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="px-4 py-3 font-medium">Financial Year</th>
                  {admin && <th className="px-4 py-3 font-medium">Employee</th>}
                  <th className="px-4 py-3 font-medium">PAN</th>
                  <th className="px-4 py-3 font-medium">Uploaded</th>
                  <th className="px-4 py-3 font-medium text-right">Download</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <Badge>{r.financial_year}</Badge>
                    </td>
                    {admin && (
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">
                          {r.user_name || '—'}
                        </div>
                        {r.dgp_id && (
                          <div className="text-xs text-gray-400">{r.dgp_id}</div>
                        )}
                      </td>
                    )}
                    <td className="px-4 py-3 font-mono text-gray-700">{r.pan_no}</td>
                    <td className="px-4 py-3 text-gray-500">
                      {r.uploaded_at ? formatDate(r.uploaded_at) : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleDownload(r)}
                        disabled={downloadingId === r.id}
                        className="inline-flex items-center gap-1 text-brand-primary hover:underline disabled:opacity-50"
                      >
                        <Download className="h-4 w-4" />
                        {downloadingId === r.id ? 'Opening…' : 'PDF'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const toneClass =
    tone === 'pos'
      ? 'text-emerald-600'
      : tone === 'neg'
      ? 'text-red-600'
      : tone === 'warn'
      ? 'text-amber-600'
      : 'text-gray-900';
  return (
    <div className="rounded-md bg-white border border-gray-200 px-3 py-2">
      <div className={`text-xl font-semibold ${toneClass}`}>{value ?? 0}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  );
}

export default Form16Page;
